<template>
    <div
        class="header-mobile"
        :class="classTopArrived"
    >
        <ButtonRound
            class="button-back"
            :class="classButtonBack"
            icon="angle-left"
            type="link"
            aria-label="Go back one level"
            @click="closeSubmenu"
        />

        <ButtonRound
            class="button-close"
            icon="close"
            type="link"
            aria-label="Close menu"
            @click="closeMenuMobile"
        />
    </div>

    <ol
        ref="mainMenuListElement"
        class="main-menu-list"
        data-qa="main-menu-list"
    >
        <template
            v-for="(menuItem, menuIndex) in mainMenu"
            :key="menuIndex"
        >
            <li
                ref="menuItems"
                class="main-menu-item"
                :class="getClassMenuItem(menuItem.hideOnDesktop)"
            >
                <button
                    v-if="currentSubmenu === -1"
                    class="mobile-main-menu-item"
                    @click.prevent.stop="openSubmenu(menuIndex)"
                >
                    {{ menuItem.title }}
                </button>

                <HeaderLink
                    v-if="currentSubmenu === -1"
                    v-qa="'main-menu-item-link'"
                    v-qa.index="menuIndex"
                    aria-haspopup="true"
                    :link="menuItem.link"
                    class="main-menu-item-link"
                    @click="closeMenuMobile"
                >
                    {{ menuItem.title }}
                </HeaderLink>

                <div
                    v-qa.sub.menu="menuIndex"
                    :class="getClassSubMenu(menuItem.subMenu, menuIndex)"
                    aria-label="submenu"
                    class="sub-menu"
                >
                    <div class="sub-menu-container">
                        <div
                            v-if="
                                menuItem.subMenu &&
                                menuItem.subMenu.title &&
                                menuItem.subMenu.description
                            "
                            class="sub-menu-description"
                        >
                            <p class="menu-title">
                                {{ menuItem.subMenu.title }}
                            </p>

                            <p class="menu-description">
                                {{ menuItem.subMenu.description }}
                            </p>
                        </div>

                        <div
                            v-if="
                                menuItem.subMenu &&
                                menuItem.subMenu.sections.length
                            "
                            class="sub-menu-sections"
                            :class="{
                                'three-columns':
                                    menuItem.subMenu.editorialContent,
                            }"
                        >
                            <SubMenuSection
                                v-for="(section, sectionIndex) in menuItem
                                    .subMenu.sections"
                                :key="sectionIndex"
                                :section="section"
                                :use-large-format-items="
                                    menuItem.subMenu.useLargeFormatItems
                                "
                                @close="closeMenuMobile"
                            />
                        </div>

                        <div
                            v-if="
                                menuItem.subMenu &&
                                menuItem.subMenu.editorialContent
                            "
                            class="sub-menu-article"
                        >
                            <HeaderLink
                                :link="menuItem.subMenu.editorialContent.link"
                                class="sub-menu-article-link"
                            >
                                <div class="sub-menu-article-image">
                                    <CmsImage
                                        :data="
                                            menuItem.subMenu.editorialContent
                                                .image
                                        "
                                        fit="cover"
                                        :sizes="['220px']"
                                    />
                                </div>

                                <span>
                                    {{ menuItem.subMenu.editorialContent.text }}
                                </span>
                            </HeaderLink>
                        </div>
                    </div>
                </div>
            </li>
        </template>
    </ol>

    <TopBar
        :class="classBottomArrived"
        class="top-bar-mobile"
        @toggle-login-form="toggleLoginForm"
        @close-menu="closeMenuMobile"
    />
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import type { HeaderMenuFragment, SubMenuFragment } from '@/graphql/api'
import { hasImageInEachItem } from './helpers'

import HeaderLink from '@/components/layout/header/menu/HeaderLink.vue'
import CmsImage from '@/components/cms/CmsImage.vue'
import SubMenuSection from './SubMenuSection.vue'
import ButtonRound from '@/components/ui/buttons/ButtonRound.vue'
import TopBar from '../TopBar.vue'

const { scrolledState } = defineProps<{
    mainMenu: HeaderMenuFragment[]

    scrolledState: {
        scrolledToBottom: boolean
        scrolledToTop: boolean
    }
}>()

const emit = defineEmits<{
    'toggle-login-form': []
    'close-menu-mobile': []
    'submenu-changed': []
}>()

const getClassSubMenu = (subMenu: SubMenuFragment | null, index: number) => ({
    'with-images-only': subMenu ? hasImageInEachItem(subMenu) : false,
    'open': index === currentSubmenu.value,
})

const openSubmenu = (index: number) => {
    currentSubmenu.value = index
}

const closeMenuMobile = () => {
    closeSubmenu()
    emit('close-menu-mobile')
}

const currentSubmenu = ref(-1)
const closeSubmenu = () => {
    currentSubmenu.value = -1
}
watch(currentSubmenu, () => emit('submenu-changed'))

const toggleLoginForm = () => emit('toggle-login-form')

const classTopArrived = computed(() => ({
    'top-arrived': scrolledState.scrolledToTop,
}))

const classBottomArrived = computed(() => ({
    'bottom-arrived': scrolledState.scrolledToBottom,
}))

const getClassMenuItem = (hideOnDesktop: boolean) => ({
    'hide-on-desktop': hideOnDesktop,
})

const classButtonBack = computed(() => ({
    'hidden': currentSubmenu.value === -1,
}))
</script>

<style lang="scss" scoped>
.header-mobile {
    --bar-height: 60px;

    top: 0;
    position: fixed;
    width: 100%;
    height: var(--bar-height);
    padding: 0 10px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background-color: $stone-3;
    z-index: 102;

    @include min-width(1024) {
        display: none;
    }

    .button-back.hidden {
        opacity: 0;
        pointer-events: none;
    }
}

.header-mobile,
.top-bar-mobile {
    --bar-height: 60px;

    height: var(--bar-height);

    box-shadow: 0 0 10px rgba(0, 0, 0, 0.25);

    transition: box-shadow 175ms var(--timing-function-easing-accelerate);

    &.top-arrived,
    &.bottom-arrived {
        box-shadow: none;
    }
}

.top-bar-mobile {
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    z-index: 103;
    display: flex;
    align-items: center;
    padding: 0 8px;

    box-shadow:
        0 -3px 10px rgba(0, 0, 0, 0.25),
        0 -1px 5px rgb(0 0 0 / 30%);

    @media (min-width: 360px) {
        padding: 0 24px;
    }
}

@include min-width(1024) {
    .top-bar-mobile {
        display: none;
    }
}

.main-menu-list {
    --bar-height: 60px;

    @media (max-width: 1023px) {
        position: fixed;
        padding: calc(var(--bar-height) + 20px) 0;
        background: $stone-3;

        width: 100%;
        left: 0;
        top: 0;
        bottom: 0;
        z-index: 100;
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
    }

    .sub-menu {
        display: none;

        &.open {
            display: block;
        }
    }

    .main-menu-item-link {
        display: none;
    }

    .mobile-main-menu-item {
        position: relative;
        width: 100%;
        height: 50px;
        padding-left: 36px;
        font-family: $effra-font-stack;

        letter-spacing: 0.05em;
        text-transform: uppercase;

        font-weight: 400;
        font-size: 0.95rem;
        color: $slater;

        @media (max-width: 359px) {
            padding-left: 24px;
        }

        &:after {
            display: block;
            position: absolute;
            top: 16px;
            right: 0;
            right: 24px;

            font-family: $icon-font;
            content: $icon-angle-right;
            font-weight: 400;
            font-size: 15px;
        }

        @include min-width(1024) {
            display: none;
        }
    }

    .main-menu-item {
        &.hide-on-desktop {
            @include min-width(1024) {
                display: none;
            }

            .mobile-main-menu-item {
                display: none;
            }

            .main-menu-item-link {
                display: flex;
                align-items: center;
                height: 50px;
                padding-left: 36px;
                padding-right: 48px;
                font-family: $effra-font-stack;

                letter-spacing: 0.05em;
                text-transform: uppercase;

                font-weight: 400;
                font-size: 0.95rem;
                color: $slater;

                @media (max-width: 359px) {
                    padding-left: 24px;
                }
            }
        }
    }

    @include min-width(1024) {
        color: $slate;
        z-index: 100;
        align-content: center;
        justify-content: center;
        display: flex;

        :deep(.main-menu-item) {
            &:hover {
                .main-menu-item-link {
                    border-bottom: 2px solid $green;
                    display: block;
                }

                .sub-menu {
                    display: flex;
                }
            }

            .main-menu-item-link {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: space-between;
                color: $slate;
                font-family: $effra-font-stack;
                letter-spacing: 0.05em;
                font-weight: 400;
                text-transform: uppercase;
                font-size: 0.8rem;
                line-height: 1.25rem;
                padding: 19px 10px 17px;
                border-bottom: 2px solid transparent;
                text-wrap: nowrap;

                @include min-width(1140) {
                    font-size: 0.875rem;
                }

                @include min-width(1360) {
                    padding-left: 15px;
                    padding-right: 15px;
                }
            }

            .sub-menu {
                padding: 0 30px 10px;
                font-family: $effra-font-stack;
                font-weight: 400;
                text-transform: uppercase;
                letter-spacing: 0.05em;
                display: none;

                @include min-width(1024) {
                    width: 100%;
                    max-width: 1140px;
                    height: 500px;
                    padding: 30px;
                    position: absolute;
                    top: 60px;
                    left: 0;
                    right: 0;
                    margin: 0 auto;
                    background: $stone-3;
                    border: 1px solid $grey-200;
                    box-shadow: 0 5px 10px rgba($black, 0.1);
                }

                &.with-images-only {
                    align-items: center;

                    .sub-menu-container {
                        align-items: center;
                    }

                    .sub-menu-sections {
                        flex-flow: row;
                        align-items: center;

                        .sub-menu-section {
                            width: 50%;
                        }
                    }
                }

                .sub-menu-container {
                    width: 1024px;
                    max-width: 100%;
                    margin: 0 auto;
                    display: flex;
                    justify-content: center;
                    align-items: flex-start;
                }

                .sub-menu-description {
                    width: 360px;
                    height: 100%;
                    flex-shrink: 0;
                    display: none;
                    padding: 20px;
                    flex-wrap: wrap;
                    justify-content: flex-end;
                    align-items: center;
                    text-align: center;

                    .menu-title {
                        width: 100%;
                        font-size: 1.5rem;
                        line-height: 1.2em;
                        font-family: $parcel-font-stack;
                        text-transform: initial;
                        letter-spacing: 0;
                        margin: 0 0 10px;
                        color: $blue;
                    }

                    .menu-description {
                        font-family: $sectra-font-stack;
                        font-size: 1rem;
                        line-height: 1.7rem;
                        text-transform: initial;
                        letter-spacing: 0;
                        height: 80px;
                        max-width: 300px;
                    }

                    @include min-width(1024) {
                        display: flex;
                    }
                }

                .sub-menu-sections {
                    width: 100%;
                    height: 500px;
                    margin: 0 auto;
                    padding: 0 0 20px;
                    display: flex;
                    flex-flow: column wrap;

                    &.three-columns {
                        .sub-menu-section {
                            @include min-width(1024) {
                                width: 33%;
                            }
                        }
                    }
                }
            }
        }
    }

    .sub-menu-article {
        width: 30%;
        flex-shrink: 0;
        display: none;
        height: 100%;

        @include min-width(1024) {
            display: block;
        }

        @include min-width(1360) {
            display: flex;
            align-items: center;
        }

        .sub-menu-article-link {
            width: 100%;
            color: $slate;
            text-align: center;

            .sub-menu-article-image {
                width: 220px;
                height: 300px;
                margin: 0 auto;
                border-radius: 110px;
                display: block;
                background-repeat: no-repeat;
                background-size: cover;
                background-position: center center;
                flex-shrink: 0;
                overflow: hidden;
                margin-bottom: 30px;
            }

            span {
                font-size: 0.875rem;
                width: 100%;
                display: block;
                position: relative;
            }
        }
    }
}
</style>
